// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import productDescriptionVariantStyles from "@synd/App/SharedStyles/ProductDescriptionStyles";
import xProductPartNumbersStyles from "@synd/App/SharedStyles/ProductPartNumbersStyles";
import { ProductDescriptionStyles } from "@synd/Overrides/Components/ProductDescription";
import { ProductPriceStyles } from "@synd/Overrides/Components/ProductPrice";
import LinkAddToList from "@synd/XDL/Icons/LinkAddToList";
import LinkRemove from "@synd/XDL/Icons/LinkRemove";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    totalWrapper?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    cartLineErrorMessageWrapper?: InjectableCss;
    notifyStyles?: TypographyProps;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 96px;
                height: 96px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    aspect-ratio: 1;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        productDetailSubDescription: {
            ...mergeWithCss(productDescriptionVariantStyles.small?.productDetailDescription, {
                css: css`
                    ${ellipsis(1)}
                    min-height: unset;
                    margin-bottom: 4px;
                    ${xScreen("lte", "md")} {
                        ${ellipsis(2)}
                    }
                `,
            }),
        },
    },
    productPartNumbers: {
        container: {
            gap: 2,
            css: css`
                flex-grow: 0;
                @media print {
                    display: block;
                    width: 100%;
                }
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles({
            icon: LinkAddToList,
            sizeVariant: "xsmall",
            variant: "primary",
            linkStyles: {
                css: css`
                    justify-content: flex-end;
                    margin-top: 12px;
                `,
                icon: {
                    iconProps: {
                        size: 12,
                    },
                },
            },
        }),
    },
    productPrice: {
        priceLabelText: {
            size: fontConfig.xSmallSingleLine.size,
            weight: fontConfig.weight.regular,
            css: css`
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        font-size: ${fontConfig.small.size}px;
                        line-height: ${fontConfig.xxSmall.lineHeight};
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                padding-top: 2px;
            `,
        },
        quoteMessageWrapper: {
            css: css`
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                padding-top: 2px;
                ${xScreen("lte", "md")} {
                    padding-top: 0;
                }
            `,
        },
        price: {
            priceText: {
                size: fontConfig.small.size,
                weight: fontConfig.weight.semiBold,
                lineHeight: fontConfig.smallSingleLine.lineHeight,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: fontConfig.xSmall.size,
                    lineHeight: fontConfig.xSmall.lineHeight,
                    weight: fontConfig.weight.regular,
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
        },
        invalidPriceLink: {
            typographyProps: {
                size: fontConfig.small.size,
                weight: fontConfig.weight.semiBold,
                lineHeight: fontConfig.smallSingleLine.lineHeight,
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: fontConfig.xSmallSingleLine.size,
                lineHeight: fontConfig.xSmallSingleLine.lineHeight,
                weight: fontConfig.weight.regular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: ${fontConfig.small.size}px;
                            line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                        }
                    }
                `,
            },
            text: {
                size: fontConfig.smallSingleLine.size,
                weight: fontConfig.weight.semiBold,
                lineHeight: fontConfig.smallSingleLine.lineHeight,
                css: css`
                    text-align: start;
                    display: flex;
                    padding-top: 2px;
                    ${xScreen("lte", "md")} {
                        padding-top: 0;
                    }
                `,
            },
            wrapper: {
                css: css`
                    ${({ isInvalidPrice }: any) =>
                        isInvalidPrice &&
                        css`
                            [data-test-selector^="text_"] {
                                color: ${color("danger")};
                            }
                        `}
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: fontConfig.xSmallSingleLine.size,
        weight: fontConfig.weight.regular,
        lineHeight: fontConfig.xSmallSingleLine.lineHeight,
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: ${fontConfig.small.size}px;
                    line-height: ${fontConfig.xxSmall.lineHeight};
                }
            }
        `,
    },
    removeCartLineIcon: {
        src: LinkRemove,
        size: 12,
        color: "common.ui9",
        css: css`
            &,
            svg {
                :hover {
                    color: ${color("common.ui10")};
                }
            }
        `,
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                & > div {
                    margin-bottom: 10px;
                }
            `,
        },
    },
    unitOfMeasure: {
        size: fontConfig.xSmall.size,
        weight: fontConfig.weight.regular,
        css: css`
            line-height: ${fontConfig.xSmall.lineHeight};
            text-transform: capitalize;
        `,
    },
    totalWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
            ${xScreen("lte", "md")} {
                align-items: end;
            }
        `,
    },
    cartLineErrorMessageWrapper: {
        css: css`
            & > span {
                margin-bottom: 8px;
            }
        `,
    },
    notifyStyles: {
        css: css`
            font-size: ${fontConfig.xxSmallSingleLine.size}px;
            line-height: ${fontConfig.xxSmallSingleLine.lineHeight};
            margin-bottom: 8px;
            width: fit-content;
            padding: 6px;
            .notify-icon-wrapper {
                margin-right: 6px;
                margin-top: 1px;
                ${IconWrapper} {
                    &,
                    svg {
                        height: 12px;
                        width: 12px;
                    }
                }
            }
            .notify-content-wrapper > ${TypographyStyle} {
                font-weight: ${fontConfig.weight.semiBold};
                margin-right: 4px;
            }
        `,
    },
};

export default cartLineSharedStyles;
